import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { CHALLENGE_METRIC } from "@/lib/constants";
import { Swords, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const empty = { title: "", description: "", metric: "hours", target: 10, points: 10, multiplier: 1, start_date: "", end_date: "" };
const F = ({ label, children }) => (<div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";
const statusMap = { upcoming: ["قادم", "bg-blue-500/15 text-blue-700 border-blue-400/30"], active: ["نشط الآن", "bg-emerald-500/15 text-emerald-700 border-emerald-400/30"], ended: ["منتهٍ", "bg-stone-500/15 text-stone-600 border-stone-400/30"] };

export default function Challenges() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState(null);

  const load = () => { api.get("/challenges").then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toIso = (v) => (v ? new Date(v).toISOString() : "");
  const save = async () => {
    if (!form.title || !form.start_date || !form.end_date) return toast.error("العنوان والتواريخ مطلوبة");
    try {
      await api.post("/challenges", { ...form, target: +form.target, points: +form.points, multiplier: +form.multiplier, start_date: toIso(form.start_date), end_date: toIso(form.end_date) });
      playSuccess();
      toast.success("تم إنشاء التحدي");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/challenges/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <InspireBanner />
      <PageTitle title="التحديات الأسبوعية" subtitle="تنافسوا في الخير… فالسبق إلى العطاء شرفٌ لا يُضاهى" icon={Swords}
        actions={isAdmin && <button data-testid="add-challenge-btn" onClick={() => { setForm(empty); setOpen(true); }} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> تحدٍ جديد</button>}
      />

      {!items.length ? <EmptyState text="لا توجد تحديات" icon={Swords} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="glass glass-hover rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <Badge className={statusMap[c.status]?.[1]}>{statusMap[c.status]?.[0]}</Badge>
                {isAdmin && <button onClick={() => setDelId(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700"><Trash2 size={15} /></button>}
              </div>
              <h4 className="font-head font-bold text-lg text-stone-800 mt-2">{c.title}</h4>
              {c.description && <p className="text-sm text-stone-500 mt-1">{c.description}</p>}
              <div className="mt-3 space-y-1 text-sm text-stone-600">
                <div className="flex justify-between"><span>المقياس</span><span className="text-amber-600">{CHALLENGE_METRIC[c.metric]}</span></div>
                <div className="flex justify-between"><span>الهدف</span><span className="text-amber-600">{c.target}</span></div>
                <div className="flex justify-between"><span>النقاط</span><span className="text-amber-600">{c.points} × {c.multiplier}</span></div>
                <div className="flex justify-between"><span>ينتهي</span><span className="text-stone-500">{c.end_date?.slice(0, 10)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">تحدٍ جديد</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><F label="عنوان التحدي"><input data-testid="challenge-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></F></div>
            <div className="sm:col-span-2"><F label="الوصف"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} rows={2} /></F></div>
            <F label="مقياس الإنجاز"><select value={form.metric} onChange={(e) => set("metric", e.target.value)} className={inputCls}>{Object.entries(CHALLENGE_METRIC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
            <F label="الهدف"><input type="number" value={form.target} onChange={(e) => set("target", e.target.value)} className={inputCls} /></F>
            <F label="النقاط"><input type="number" value={form.points} onChange={(e) => set("points", e.target.value)} className={inputCls} /></F>
            <F label="المضاعفة"><input type="number" step="0.5" value={form.multiplier} onChange={(e) => set("multiplier", e.target.value)} className={inputCls} /></F>
            <F label="تاريخ البداية"><input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} /></F>
            <F label="تاريخ النهاية"><input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} /></F>
          </div>
          <DialogFooter><button data-testid="save-challenge-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">إنشاء</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">حذف التحدي</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
